import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Download, Mail, CheckCircle2, Users } from "lucide-react";

/** Tiny live counter — uses real backend stats only, no inflation.
 *  Shows nothing if there is no meaningful data to display. */
const SocialProofPill = ({ className = "" }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/lead-magnet/stats")
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!stats) return null;
  // Prefer "today" if non-zero, otherwise fall back to "this week", otherwise total.
  let label = null;
  if (stats.today > 0) {
    label = `${stats.today} personne${stats.today > 1 ? "s ont" : " a"} téléchargé le PDF aujourd'hui`;
  } else if (stats.this_week > 0) {
    label = `${stats.this_week} personne${stats.this_week > 1 ? "s ont" : " a"} téléchargé le PDF cette semaine`;
  } else if (stats.total > 0) {
    label = `${stats.total} téléchargement${stats.total > 1 ? "s" : ""} à ce jour`;
  }
  if (!label) return null;
  return (
    <div
      data-testid="lead-magnet-social-proof"
      className={`inline-flex items-center gap-2 bg-white border border-[#34D399]/40 text-[#1A2A40] rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm ${className}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#34D399] opacity-75"></span>
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#34D399]"></span>
      </span>
      <Users className="w-3.5 h-3.5 text-[#34D399]" />
      <span>{label}</span>
    </div>
  );
};

/**
 * Lead magnet capture form — emits the PDF "50 phrases japonaises voyage" link
 * to the user's email + triggers a 4-step nurture sequence.
 */
export const LeadMagnetSection = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const compactInputRef = useRef(null);
  const fullInputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    // Read directly from the DOM to catch browser autofill (Safari iOS,
    // 1Password, Google autofill on Android) which sometimes populates the
    // <input value> without firing onChange, leaving the React state empty.
    const domValue = (compactInputRef.current?.value || fullInputRef.current?.value || "").trim();
    const effective = domValue || email.trim();
    if (!effective || !effective.includes("@") || effective.length < 5) {
      toast.error("Merci d'entrer un email valide.");
      return;
    }
    // Sync React state so the confirmation screen shows the real address.
    if (effective !== email) setEmail(effective);
    setLoading(true);
    try {
      await api.post("/lead-magnet/request", { email: effective });
      setSent(true);
      toast.success("📕 PDF envoyé ! Vérifie ta boîte mail.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <div
        data-testid="lead-magnet-compact"
        className="bg-[#FFF5E0] border-2 border-[#FFD166] rounded-2xl p-6 my-8"
      >
        <div className="flex items-center gap-3 mb-3">
          <Download className="w-6 h-6 text-[#E94A3F]" />
          <h3 className="font-display font-black text-xl text-[#1A2A40] m-0">
            📕 Reçois gratuitement notre PDF
          </h3>
        </div>
        <SocialProofPill className="mb-3" />
        <p className="text-sm text-[#4A5568] mb-4">
          <strong>50 phrases japonaises essentielles pour voyager au Japon</strong> : politesses,
          restaurant, hôtel, urgences. 14 pages, format A4 prêt à imprimer.
        </p>
        {sent ? (
          <div className="flex items-center gap-2 text-[#34D399] font-bold">
            <CheckCircle2 className="w-5 h-5" /> Email envoyé à {email}
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              ref={compactInputRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              data-testid="lead-magnet-email-input-compact"
              className="flex-1 px-4 py-3 rounded-xl border-2 border-[#1A2A40]/15 focus:outline-none focus:border-[#E94A3F] bg-white"
            />
            <button
              type="submit" disabled={loading}
              data-testid="lead-magnet-submit-compact"
              className="px-5 py-3 bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold rounded-xl disabled:opacity-60 whitespace-nowrap"
            >
              {loading ? "Envoi…" : "Recevoir le PDF →"}
            </button>
          </form>
        )}
      </div>
    );
  }

  // Full landing-page section
  return (
    <section
      data-testid="lead-magnet-section"
      className="relative py-20 px-6 sm:px-12 bg-gradient-to-br from-[#FFF5E0] via-[#FDFBF7] to-[#FFF5F0]"
    >
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        {/* PDF mockup */}
        <div className="relative">
          <div className="absolute -top-4 -left-4 w-full h-full bg-[#E94A3F] rounded-3xl"></div>
          <div className="relative bg-white rounded-3xl shadow-brut p-8 border-2 border-[#1A2A40]/10">
            <div className="font-jp text-sm text-[#E94A3F] mb-2 tracking-widest">日本語、一歩ずつ</div>
            <h3 className="font-display font-black text-3xl text-[#1A2A40] leading-tight mb-3">
              50 phrases japonaises<br />
              <span className="text-[#E94A3F]">pour voyager au Japon</span>
            </h3>
            <p className="text-xs text-[#A0AEC0] italic mb-6">Un guide offert par Japonais Pas à Pas</p>
            <ul className="space-y-2 text-sm text-[#1A2A40]">
              <li className="flex gap-2"><span>🙏</span> 10 politesses essentielles</li>
              <li className="flex gap-2"><span>✈️</span> Aéroport &amp; transports</li>
              <li className="flex gap-2"><span>🍣</span> Au restaurant (avec wasabi !)</li>
              <li className="flex gap-2"><span>🏨</span> À l'hôtel</li>
              <li className="flex gap-2"><span>🛍️</span> Dans les magasins</li>
              <li className="flex gap-2"><span>🚨</span> En cas d'urgence</li>
              <li className="flex gap-2"><span>🌸</span> Phrases qui touchent les Japonais</li>
            </ul>
            <div className="mt-6 pt-4 border-t border-[#1A2A40]/10 text-xs text-[#A0AEC0] italic">
              14 pages · A4 · PDF
            </div>
          </div>
        </div>

        {/* Capture form */}
        <div>
          <div className="inline-flex items-center gap-2 bg-[#E94A3F] text-white px-3 py-1 rounded-full text-xs font-black tracking-widest mb-5">
            🎁 GRATUIT · CADEAU
          </div>
          <SocialProofPill className="ml-2 mb-5 align-middle" />
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A2A40] leading-tight mb-4">
            Tu pars au Japon ?<br />
            Reçois <span className="text-[#E94A3F]">notre PDF gratuit</span>
          </h2>
          <p className="text-[#4A5568] text-base sm:text-lg leading-relaxed mb-8">
            <strong>50 phrases japonaises essentielles</strong> classées par situation
            (politesses, restaurant, hôtel, urgences…), avec leur prononciation romaji
            et leur traduction française. Imprime-le et glisse-le dans ton passeport.
          </p>

          {sent ? (
            <div data-testid="lead-magnet-success" className="bg-white border-2 border-[#34D399] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-7 h-7 text-[#34D399]" />
                <h3 className="font-display font-black text-2xl text-[#1A2A40] m-0">PDF envoyé !</h3>
              </div>
              <p className="text-[#4A5568]">
                Tu vas recevoir un email à <strong>{email}</strong> avec le lien de téléchargement.
                Pense à vérifier ton dossier <em>Spam</em>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="lead-magnet-form">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  ref={fullInputRef}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ton@email.com"
                  data-testid="lead-magnet-email-input"
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-[#1A2A40]/15 focus:outline-none focus:border-[#E94A3F] bg-white text-base"
                />
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="lead-magnet-submit"
                className="w-full bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60 text-base"
              >
                {loading ? "Envoi…" : (
                  <><Download className="w-5 h-5" /> Recevoir mon PDF gratuit</>
                )}
              </button>
              <p className="text-xs text-[#A0AEC0] text-center pt-1">
                Email instantané. Pas de spam, désabonnement en 1 clic.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadMagnetSection;
